import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';

import { AuthService } from './auth.service';
import { AliceOnboardingView, DdrService } from './ddr.service';

/** Scoped by user id: a shared browser must not carry one account's
 *  dismissal over to the next account that signs in. */
const introSeenKey = (userId: number) => `ciq.aliceOnboarding.introSeen.${userId}`;

/** Alice's forced-hiring script, as the client sees it (2026-09-15). One
 *  place for the state so the sidebar nudge, the composer dice, the
 *  "Meet your team" popup and the room panel agree. */
@Injectable({ providedIn: 'root' })
export class AliceOnboardingService {
    private readonly ddr = inject(DdrService);
    private readonly auth = inject(AuthService);

    readonly state = signal<AliceOnboardingView | null>(null);
    readonly loaded = signal(false);
    readonly active = computed(() => this.state()?.active === true);
    readonly roleTitle = computed(() => this.state()?.role_title ?? null);
    readonly steps = computed(() => this.state()?.steps ?? null);
    readonly introSeen = signal(readIntroSeen(this.auth.state().user?.user_id ?? null));

    /** Last-writer-wins guard for overlapping reads. The login-time fetch and
     *  the roster-triggered re-read (meet-your-team-dialog) can be in flight
     *  together on a brand-new account; without this the older response,
     *  answered before provisioning wrote the progress row, landed last and
     *  overwrote active=true with active=false for good. A direct apply()
     *  (a mutation response carrying the view) also bumps it, so a stale read
     *  cannot undo fresher state either. */
    private refreshGeneration = 0;

    /** The signed-in user the state was read for. Plain field: the effect's
     *  own bookkeeping, never rendered. */
    private stateUserId: number | null | undefined = undefined;

    /** Root singleton, so the state outlives a sign-out and a sign-in as
     *  another account with no page reload. Every identity change re-reads
     *  the intro flag for the new account; signed out reads the default.
     *  Leaving a signed-in identity also forgets everything read for it:
     *  `loaded` gates the login-time fetch, and the roster re-read stops once
     *  a read has said the script is inactive, so state kept from account A
     *  would suppress both for account B and show A's nudge. Arriving from no identity (first sign-in,
     *  sign-in after sign-out) clears nothing, because a read for the new
     *  account may already be in flight. */
    // noinspection JSUnusedLocalSymbols
    private readonly _identityEffect = effect(() => {
        const userId = this.auth.state().user?.user_id ?? null;
        const previous = this.stateUserId;
        this.stateUserId = userId;
        if (previous === userId) {
            return;
        }
        untracked(() => this.introSeen.set(readIntroSeen(userId)));
        if (previous == null) {
            return;
        }
        untracked(() => this.clearAllState());
    });

    /** Back to the not-yet-read state. Bumps the generation so a read that
     *  left for the previous identity is dropped when it lands. */
    clearAllState(): void {
        this.refreshGeneration++;
        this.state.set(null);
        this.loaded.set(false);
    }

    apply(view: AliceOnboardingView | null | undefined): void {
        this.refreshGeneration++;
        this.state.set(view && view.active ? view : null);
        this.loaded.set(true);
    }

    async refresh(): Promise<void> {
        const generation = ++this.refreshGeneration;
        try {
            const view = await this.ddr.getAliceOnboarding();
            if (generation !== this.refreshGeneration) {
                return;
            }
            this.apply(view);
        } catch {
            this.loaded.set(true);
        }
    }

    async suggestName(): Promise<string | null> {
        try {
            return (await this.ddr.suggestOnboardingName()).name || null;
        } catch {
            return null;
        }
    }

    markIntroSeen(): void {
        this.introSeen.set(true);
        const userId = this.auth.state().user?.user_id;
        if (userId == null) {
            return;
        }
        try {
            localStorage.setItem(introSeenKey(userId), '1');
        } catch {
            // Private mode: the signal still holds for the session.
        }
    }
}

function readIntroSeen(userId: number | null): boolean {
    if (userId == null) {
        return false;
    }
    try {
        return localStorage.getItem(introSeenKey(userId)) === '1';
    } catch {
        return false;
    }
}
